<script setup lang="ts">
import { defineProps, computed, ref } from "vue";
import { HourRating, HourReport } from "@/types/calculators";
import HourDaoPathComponent from "@/views/Calculators/PersonalDates/components/dayReport/HourDaoPathComponent.vue";
import RatingBadgeComponent from "@/views/Calculators/PersonalDates/components/dayReport/RatingBadgeComponent.vue";
import SpiritsAndEliteHoursComponent from "@/views/Calculators/PersonalDates/components/dayReport/SpiritsAndEliteHoursComponent.vue";
import TenGodsComponent from "@/views/Calculators/PersonalDates/components/dayReport/TenGodsComponent.vue";

const props = defineProps<{
  timeOffset: number;
  hours: HourReport[];
}>();

const tableRowClasses: Partial<Record<HourRating, string>> = {
  excellent: "bg-[#F6FAF7]",
  bad: "bg-[#FDF9F9]",
};

const currentFiler = ref<"all" | "favourable" | "yellowPath">("all");
const filteredTableRows = computed(() => {
  if (currentFiler.value === "all") {
    return props.hours;
  }
  if (currentFiler.value === "favourable") {
    return props.hours.filter(
      (hour) =>
        hour.personal.personal_rating === "excellent" ||
        hour.personal.personal_rating === "good"
    );
  }
  if (currentFiler.value === "yellowPath") {
    return props.hours.filter(
      (hour) => hour.general.huang_hei_dao.path_type === "HuangDao"
    );
  }
  return [];
});
</script>

<template>
  <div
    class="bg-white rounded-2xl border border-divider p-4 sm:p-6 shadow-sm space-y-5"
  >
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-divider"
    >
      <!-- TITLE -->
      <div>
        <h2
          class="text-base sm:text-lg font-bold text-brown flex flex-wrap items-center gap-2"
        >
          <span>
            {{
              $t("calculators.personal-date.full-day-report.hourlyTable.title")
            }}
          </span>
          <span class="text-xs font-normal text-muted font-caudex">
            (時辰定局)
          </span>
          <span
            class="text-xs px-2 py-0.5 rounded bg-active/10 text-active font-semibold"
          >
            {{
              $t(
                "calculators.personal-date.full-day-report.hourlyTable.solarTimeOffset",
                { offset: timeOffset }
              )
            }}
          </span>
        </h2>
      </div>
      <!-- FILTER BUTTONS -->
      <div
        class="flex items-center gap-1 p-1 bg-secondary rounded-xl border border-divider text-xs self-start sm:self-auto"
      >
        <button
          class="px-2.5 sm:px-3 py-1 rounded-lg transition-colors"
          :class="
            currentFiler === 'all'
              ? 'bg-white font-bold text-brown shadow-sm'
              : 'text-muted hover:text-brown'
          "
          @click="currentFiler = 'all'"
        >
          {{
            $t(
              "calculators.personal-date.full-day-report.hourlyTable.filterAll"
            )
          }}
        </button>
        <button
          class="px-2.5 sm:px-3 py-1 rounded-lg transition-colors"
          :class="
            currentFiler === 'favourable'
              ? 'bg-white font-bold text-success shadow-sm'
              : 'text-muted hover:text-brown'
          "
          @click="currentFiler = 'favourable'"
        >
          {{
            $t(
              "calculators.personal-date.full-day-report.hourlyTable.filterFavorable"
            )
          }}
        </button>
        <button
          class="text-muted hover:text-brown px-2.5 sm:px-3 py-1 rounded-lg transition-colors"
          :class="
            currentFiler === 'yellowPath'
              ? 'bg-white font-bold text-gold shadow-sm'
              : 'text-muted hover:text-brown'
          "
          @click="currentFiler = 'yellowPath'"
        >
          {{
            $t(
              "calculators.personal-date.full-day-report.hourlyTable.filterYellowPath"
            )
          }}
        </button>
      </div>
    </div>
    <!-- MOBILE TABLE -->
    <div class="grid grid-cols-1 gap-3 md:hidden">
      <div
        v-for="tableRow in filteredTableRows"
        :key="tableRow.hour_index"
        class="p-3.5 rounded-xl border border-divider space-y-2 hover:bg-hover"
        :class="tableRowClasses[tableRow.personal.personal_rating] ?? ''"
      >
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="text-base font-bold text-brown font-caudex">
              {{ tableRow.general.gan_zhi }}
            </span>
            <span class="text-xs text-muted">
              {{ tableRow.hour_index }}.{{ tableRow.general.animal }}
            </span>
          </div>
          <RatingBadgeComponent
            :personalRating="tableRow.personal.personal_rating"
          />
        </div>
        <div
          class="flex items-center justify-between text-xs bg-white/80 px-2.5 py-1.5 rounded-lg border border-divider/60"
        >
          <div>
            <i18n-t
              keypath="calculators.personal-date.full-day-report.hourlyTable.absoluteTime"
              class="text-muted"
              tag="span"
            >
              <template #time>
                <strong class="text-brown">
                  {{ tableRow.time_range_standard }}
                </strong>
              </template>
            </i18n-t>
          </div>
          <div class="font-mono text-[11px] text-muted">
            {{
              $t(
                "calculators.personal-date.full-day-report.hourlyTable.solarTime",
                { time: tableRow.time_range_solar }
              )
            }}
          </div>
        </div>
        <div class="flex flex-wrap gap-1 items-center">
          <HourDaoPathComponent
            :huangHeiDaoInfo="tableRow.general.huang_hei_dao"
          />
          <SpiritsAndEliteHoursComponent :general="tableRow.general" />
        </div>
        <div class="text-[11px] pt-1 border-t border-divider/60">
          <TenGodsComponent :personal="tableRow.personal" />
        </div>
      </div>
    </div>
    <!-- DESKTOP TABLE -->
    <div class="hidden md:block overflow-x-auto">
      <table class="w-full text-left text-xs border-collapse">
        <thead>
          <tr
            class="border-b border-divider bg-secondary text-muted uppercase text-[11px]"
          >
            <th class="py-3 px-3 font-semibold">
              {{
                $t(
                  "calculators.personal-date.full-day-report.hourlyTable.colHour"
                )
              }}
            </th>
            <th class="py-3 px-3 font-semibold">
              {{
                $t(
                  "calculators.personal-date.full-day-report.hourlyTable.colTime"
                )
              }}
            </th>
            <th class="py-3 px-3 font-semibold">
              {{
                $t(
                  "calculators.personal-date.full-day-report.hourlyTable.colPath"
                )
              }}
            </th>
            <th class="py-3 px-3 font-semibold">
              {{
                $t(
                  "calculators.personal-date.full-day-report.hourlyTable.colSpirits"
                )
              }}
            </th>
            <th class="py-3 px-3 font-semibold">
              {{
                $t(
                  "calculators.personal-date.full-day-report.hourlyTable.colBaziResonance"
                )
              }}
            </th>
            <th class="py-3 px-3 font-semibold text-right min-w-[117px]">
              {{
                $t(
                  "calculators.personal-date.full-day-report.hourlyTable.colRating"
                )
              }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-divider">
          <tr
            v-for="tableRow in filteredTableRows"
            :key="tableRow.hour_index"
            class="transition-colors hover:bg-hover"
            :class="tableRowClasses[tableRow.personal.personal_rating] ?? ''"
          >
            <td class="py-3.5 px-3">
              <div class="font-bold text-brown text-sm font-caudex">
                {{ tableRow.general.gan_zhi }}
              </div>
              <div class="text-[11px] text-muted">
                {{ tableRow.hour_index }}.{{ tableRow.general.animal }}
              </div>
            </td>
            <td class="py-3.5 px-3">
              <div class="font-semibold text-brown">
                {{ tableRow.time_range_standard }}
              </div>
              <div class="text-[11px] text-muted font-mono">
                {{
                  $t(
                    "calculators.personal-date.full-day-report.hourlyTable.solarTime",
                    { time: tableRow.time_range_solar }
                  )
                }}
              </div>
            </td>
            <td class="py-3.5 px-3">
              <HourDaoPathComponent
                :huangHeiDaoInfo="tableRow.general.huang_hei_dao"
              />
            </td>
            <td class="py-3.5 px-3">
              <SpiritsAndEliteHoursComponent :general="tableRow.general" />
            </td>
            <td class="py-3.5 px-3">
              <div class="space-y-0.5">
                <TenGodsComponent :personal="tableRow.personal" />
              </div>
            </td>
            <td class="py-3.5 px-3 text-right">
              <RatingBadgeComponent
                :personalRating="tableRow.personal.personal_rating"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
